/**
 * Copy rich content to the clipboard as both `text/html` and `text/plain`.
 *
 * The ClipboardItem is built synchronously so Safari/WebKit still sees the
 * originating user gesture. Browsers without `ClipboardItem` /
 * `navigator.clipboard.write`, and rejected rich writes, fall back to
 * `writeText(text)` so plain-text destinations still get the content.
 * If the fallback fails too the error propagates, leaving the caller's
 * existing failure handling authoritative.
 */
export async function copyRichText({ html, text }) {
  const plain = text || '';

  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return;
    } catch (_err) {
      // Unsupported flavor, denied permission or lost user activation —
      // fall through to the plain-text path below.
    }
  }

  await navigator.clipboard.writeText(plain);
}

/**
 * Write to clipboard with deferred content.
 *
 * Reserves the clipboard immediately (within user gesture) using a
 * ClipboardItem with a pending promise-blob, then fills the content
 * when asyncFn resolves. While pending, paste yields nothing.
 *
 * Falls back to clear-then-write for browsers without ClipboardItem support.
 */
export async function clipboardWriteDeferred(asyncFn) {
  let resolveClipboard;
  let writePromise;

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const textPromise = new Promise(r => { resolveClipboard = r; });
    try {
      writePromise = navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textPromise.then(t => new Blob([t], { type: 'text/plain' })),
        }),
      ]);
    } catch {
      resolveClipboard = null;
    }
  }

  if (resolveClipboard) {
    try {
      const text = await asyncFn();
      resolveClipboard(text || '');
      await writePromise;
      return text;
    } catch (err) {
      resolveClipboard('');
      try { await writePromise; } catch {}
      throw err;
    }
  }

  try { await navigator.clipboard.writeText(''); } catch {}
  const text = await asyncFn();
  if (text) {
    await navigator.clipboard.writeText(text);
  }
  return text;
}
